import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setHomepageModules, toggleHomepageModule, updateHomepageFooter, updateHomepageHeader, updateHomepageModule } from '../../../../../../store/actions/admin/create-event';
import { EventsState } from '../../../../../../store/types';
import { HomepageFooter, HomepageHeader, LanguagesAbbr, ModuleMap, PageModule, PageModuleType } from '../../../../../../types/working-model';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import EditLayoutModal from '../../../homepage/navigation/structure/edit-module-layout-modal/edit-module-layout-modal';
import './controls.scss';
import EditFooterModal from './edit-footer-modal';
import EditHeaderModal from './edit-header-modal';
import EditSectionColorsModal, { EContentColorOverrideNames } from './edit-section-colors-modal';

export interface InitialControlsDisplay {
	editColors?: boolean;
	editLayout?: boolean;
	addContent?: boolean;
	changeSectionPosition?: boolean;
	removeSection?: boolean;
}

const InitialControlsDisplay = {
	editColors: true,
	editLayout: true,
	addContent: true,
	changeSectionPosition: true,
	removeSection: false,
};

export interface IEditableContent {
	jsx: JSX.Element;
	onDone: () => void;
}

interface Props {
	openContentModal?: () => void;
	openEditModal?: () => void;
	module: PageModule | HomepageFooter | HomepageHeader;
	contentButtonText?: string;
	editableContent?: IEditableContent;
	display?: InitialControlsDisplay;
	addContentIconAsEditIcon?: boolean //makes the add content button use the edit icon
	baseLanguage: LanguagesAbbr;
	language: LanguagesAbbr;
}

const Controls: React.FC<Props> = (props: Props) => {
	const {
		module,
		openContentModal,
		contentButtonText,
		editableContent,
		display = InitialControlsDisplay,
		addContentIconAsEditIcon = false,
		baseLanguage,
		language
	} = props;

	const { workingEvent } = useSelector((state: EventsState) => state.CreateEventReducer);

	const dispatch = useDispatch();

	const [openLayoutModal, setOpenLayoutModal] = useState<PageModuleType | null>(null);
	const [openStyleModal, setOpenStyleModal] = useState(false);
	const [workingPageModule, setWorkingPageModule] = useState<PageModule | null>();
	const [openFooterModal, setOpenFooterModal] = useState<boolean>(false);
	const [openHeaderModal, setOpenHeaderModal] = useState<boolean>(false);
	const [selectedModuleId, setSelectedModuleId] = useState<PageModuleType | null>(null);
	const [title, setTitle] = useState('');

	useEffect(() => {
		if (hasPageModuleControls(module)) setWorkingPageModule(module);
	}, [module]);

	function makeBackgroundStylingOverrides(colorKey: string) {
		const isColor = !colorKey.includes('.');
		const isRestoringDefault = colorKey === "";

		//If we are restoreing default we want it to just be a blank string, if changing color we want the classname with appended"-background"
		if (isColor && !isRestoringDefault) {
			colorKey += "-background";
		}
		return {
			color: (module as PageModule)?.styling_overrides?.background?.color ?? '',
			image: { is_on: false, src: (module as PageModule)?.styling_overrides?.background?.image.src ?? '' },
			[isColor ? 'color' : 'image']: isColor ? colorKey : { is_on: true, src: colorKey }
		};
	}

	function updateModuleBackground(dispatchFunc: any, background: any) {
		dispatch(dispatchFunc({
			...module,
			styling_overrides: {
				...(module as PageModule).styling_overrides,
				background
			}
		}));
	}

	function handleFinishStyling(colorKey: string) {
		const background = makeBackgroundStylingOverrides(colorKey);

		if (hasPageModuleControls(module)) {
			updateModuleBackground(updateHomepageModule, background);
		}
		if (hasHeaderControls(module)) {
			updateModuleBackground(updateHomepageHeader, background);
		}
		if (hasFooterControls(module)) {
			updateModuleBackground(updateHomepageFooter, background);
		}
		setOpenStyleModal(false);
	}

	const handleCancelStyling = useCallback(() => {
		setOpenStyleModal(false);
	}, []);

	function openEditModal() {

		if (hasPageModuleControls(module)) {
			setSelectedModuleId(module?.id || 0);
			switch (module.type) {
				case PageModuleType.main_event_banner:
					setOpenLayoutModal(PageModuleType.main_event_banner);
					break;
				case PageModuleType.speakers:
					setOpenLayoutModal(PageModuleType.speakers);
					break;
				case PageModuleType.about:
					setOpenLayoutModal(PageModuleType.about);
					break;
				case PageModuleType.agenda:
					setOpenLayoutModal(PageModuleType.agenda);
					break;
				case PageModuleType.products:
					setOpenLayoutModal(PageModuleType.products);
					break;
				case PageModuleType.sponsors:
					setOpenLayoutModal(PageModuleType.sponsors);
					break;
				case PageModuleType.videos:
					setOpenLayoutModal(PageModuleType.videos);
					break;
				case PageModuleType.callout:
					setOpenLayoutModal(PageModuleType.callout);
					break;
				case PageModuleType.blank:
					setOpenLayoutModal(PageModuleType.blank);
					break;
				default:
					setOpenLayoutModal(PageModuleType.main_event_banner);
			}
			setTitle(ModuleMap[module.type]);
		}

		// check for header first because footers only property is logo and header modules have logos as well
		if (hasHeaderControls(module)) {
			setTitle('Header');
			setOpenHeaderModal(true);
			return;
		}
		if (hasFooterControls(module)) {
			setTitle('Footer');
			setOpenFooterModal(true);
		}
	}

	function changeModuleIndex(i: number, moveModule: PageModule): void {
		const _modules = workingEvent!.homepage!.modules.slice();
		const startIndex = _modules.findIndex((_module: PageModule) => Number(_module.id) === Number(moveModule.id));
		// i will either be 1 or -1, +- = -
		// -1 brings it up in the array
		// 1 pushes it down in the array
		const newIndex = startIndex + i;

		const [module] = _modules.splice(startIndex, 1);
		_modules.splice(newIndex, 0, module);

		dispatch(setHomepageModules(_modules));
	}
	return (
		<div className="section-controls-wrapper">
			<div className="button-container">
				{
					display.editColors &&
					<button
						className="round"
						onClick={() => setOpenStyleModal(true)}
					>
						<Icon name={ICONS.PAINTBRUSH} size={16} color={COLORS.WHITE} />
					</button>
				}
				{
					display.editLayout &&
					<button
						className="round"
						onClick={openEditModal}
					>
						<Icon name={ICONS.EDIT} size={16} color={COLORS.WHITE} backgroundColor={COLORS.DARK_GREY} />
					</button>
				}
				{
					display.addContent &&
					<button
						className="add-content small"
						// openModal changes to openContentModuleModal, same functionality tho
						onClick={openContentModal}
					>
						<Icon name={addContentIconAsEditIcon ? ICONS.EDIT : ICONS.ADD} size={16} color={COLORS.WHITE} backgroundColor={COLORS.DARK_GREY} />
						<span>{contentButtonText}</span>
					</button>
				}
				{
					display.changeSectionPosition && hasPageModuleControls(module) &&
					<>
						<button
							className="round"
							onClick={() => changeModuleIndex(-1, module)}
						>
							<Icon name={ICONS.ARROW_UP_THIN} size={16} color={COLORS.WHITE} backgroundColor={COLORS.DARK_GREY} />
						</button>
						<button
							className="round arrow-down"
							onClick={() => changeModuleIndex(1, module)}
						>
							<Icon name={ICONS.ARROW_UP_THIN} size={16} color={COLORS.WHITE} backgroundColor={COLORS.DARK_GREY} />
						</button>
					</>
				}
				{
					display.removeSection && hasPageModuleControls(module) &&
					<button
						className="round"
						onClick={() => dispatch(toggleHomepageModule(module))}
					>
						<Icon name={ICONS.DELETE} size={16} color={COLORS.WHITE} backgroundColor={COLORS.DARK_GREY} />
					</button>
				}
			</div>

			{hasPageModuleControls(module) &&
				<EditLayoutModal
					open={!!openLayoutModal || openLayoutModal === 0}
					setOpenLayoutModal={setOpenLayoutModal}
					pageModuleType={openLayoutModal}
					selectedModule={selectedModuleId}
					title={title}
					editModule={editableContent}
				/>
			}
			{hasFooterControls(module) &&
				<EditFooterModal
					open={openFooterModal}
					closeModal={() => setOpenFooterModal(false)}
					baseLanguage={baseLanguage}
					language={language}
				/>
			}
			{hasHeaderControls(module) &&
				<EditHeaderModal
					open={openHeaderModal}
					closeModal={() => setOpenHeaderModal(false)}
					baseLanguage={baseLanguage}
					language={language}
				/>
			}

			<EditSectionColorsModal
				open={openStyleModal}
				title={EContentColorOverrideNames.background}
				backgroundImage={workingPageModule?.styling_overrides?.background?.image.src}
				onFinish={handleFinishStyling}
				onClose={handleCancelStyling}
			/>

		</div>
	);
};

export default Controls;

export function hasPageModuleControls(_module: PageModule | HomepageHeader | HomepageFooter): _module is PageModule {
	// check to see if module has key that only lives on PageModule types
	return (_module as PageModule)?.content_modules !== undefined;
}

export function hasFooterControls(_module: PageModule | HomepageHeader | HomepageFooter | PageModuleType): _module is HomepageFooter {
	// if _module has a callToAction then this will fail
	// and we know its not a footer
	const isNotHeader = !hasHeaderControls(_module);
	return isNotHeader && (_module as HomepageFooter)?.logo !== undefined;
}

export function hasHeaderControls(_module: PageModule | HomepageHeader | HomepageFooter | PageModuleType): _module is HomepageHeader {
	return (_module as HomepageHeader)?.callToAction !== undefined;
}